import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  organizations as organizationsApi,
  questionLibrary,
  sessions as sessionsApi,
} from "../api/resources";
import {
  Card,
  EmptyState,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHeader,
  Progress,
  StatusBadge,
} from "../components/ui";
import { useCurrentOrganization } from "../lib/OrganizationContext";
import { relativeTime } from "../lib/format";
import { useAsync } from "../lib/useAsync";

const SESSION_TYPES = [
  { value: "organization_onboarding", label: "Organization onboarding", applies: "organization" },
  { value: "department_discovery", label: "Department discovery", applies: "department" },
  { value: "employee_discovery", label: "Employee workflow discovery", applies: "person" },
];

export function InterviewsPage() {
  const { organizationId, project } = useCurrentOrganization();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const [sessionType, setSessionType] = useState(SESSION_TYPES[0].value);
  const [title, setTitle] = useState("");
  const [personId, setPersonId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  const sessions = useAsync(
    () =>
      organizationId
        ? sessionsApi.list(organizationId, { limit: 100 })
        : Promise.resolve({ items: [], total: 0, limit: 0, offset: 0 }),
    [organizationId],
  );
  const modules = useAsync(() => questionLibrary.modules(), []);
  const people = useAsync(
    () => (organizationId ? organizationsApi.people(organizationId) : Promise.resolve([])),
    [organizationId],
  );
  const departments = useAsync(
    () => (organizationId ? organizationsApi.departments(organizationId) : Promise.resolve([])),
    [organizationId],
  );

  if (!organizationId) {
    return (
      <Card>
        <EmptyState title="No organization selected" description="Create an organization first." />
      </Card>
    );
  }

  const appliesTo = SESSION_TYPES.find((row) => row.value === sessionType)?.applies ?? "organization";
  const availableModules = (modules.data ?? []).filter(
    (module) => module.applies_to === appliesTo,
  );

  async function createSession() {
    setError(undefined);
    try {
      const created = await sessionsApi.create(organizationId!, {
        onboarding_project_id: project?.id,
        module_keys: selectedModules.length
          ? selectedModules
          : availableModules.map((module) => module.key),
        session_type: sessionType,
        title: title.trim() || undefined,
        person_id: personId || undefined,
        department_id: departmentId || undefined,
      });
      setCreating(false);
      navigate(`/interviews/${created.id}`);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  const rows = sessions.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Interviews"
        description="Every interview is resumable. Answers are evidence, not conclusions."
        actions={<button className="primary" onClick={() => setCreating(true)}>New interview</button>}
      />

      <div className="stack">
        <ErrorBanner error={sessions.error ?? error} />

        {sessions.loading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Card>
            <EmptyState
              title="No interviews yet"
              description="Start with the organization onboarding interview, then run department and employee interviews."
              action={
                <button className="primary" onClick={() => setCreating(true)}>
                  New interview
                </button>
              }
            />
          </Card>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Interview</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th style={{ width: 180 }}>Progress</th>
                    <th className="numeric">Modules</th>
                    <th>Last activity</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <Link to={`/interviews/${row.id}`}>{row.title}</Link>
                      </td>
                      <td className="small muted">{row.session_type.replace(/_/g, " ")}</td>
                      <td>
                        <StatusBadge status={row.status} />
                      </td>
                      <td>
                        <Progress value={row.progress_percent} />
                      </td>
                      <td className="numeric">{row.module_keys.length}</td>
                      <td className="small muted">{relativeTime(row.last_activity_at)}</td>
                      <td>
                        <Link className="btn btn--small" to={`/interviews/${row.id}`}>
                          {["paused", "in_progress"].includes(row.status) ? "Resume" : "Open"}
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {creating ? (
        <Modal
          title="New interview"
          onClose={() => setCreating(false)}
          footer={
            <>
              <button onClick={() => setCreating(false)}>Cancel</button>
              <button className="primary" onClick={() => void createSession()}>
                Create &amp; open
              </button>
            </>
          }
        >
          <Field label="Interview type">
            <select
              value={sessionType}
              onChange={(event) => {
                setSessionType(event.target.value);
                setSelectedModules([]);
              }}
            >
              {SESSION_TYPES.map((row) => (
                <option key={row.value} value={row.value}>
                  {row.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Title" hint="Optional — defaults to the organization and module name">
            <input value={title} onChange={(event) => setTitle(event.target.value)} />
          </Field>

          {appliesTo === "person" ? (
            <Field label="Person">
              <select value={personId} onChange={(event) => setPersonId(event.target.value)}>
                <option value="">Not specified</option>
                {(people.data ?? []).map((person) => (
                  <option key={String(person.id)} value={String(person.id)}>
                    {String(person.full_name)}
                    {person.title ? ` — ${String(person.title)}` : ""}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          {appliesTo === "department" ? (
            <Field label="Department">
              <select value={departmentId} onChange={(event) => setDepartmentId(event.target.value)}>
                <option value="">Not specified</option>
                {(departments.data ?? []).map((department) => (
                  <option key={String(department.id)} value={String(department.id)}>
                    {String(department.name)}
                  </option>
                ))}
              </select>
            </Field>
          ) : null}

          <Field label="Modules" hint="Leave all unchecked to include every applicable module">
            <div className="stack" style={{ gap: 4, maxHeight: 260, overflowY: "auto" }}>
              {availableModules.map((module) => (
                <label key={module.key} className="row" style={{ gap: 8, flexWrap: "nowrap" }}>
                  <input
                    type="checkbox"
                    style={{ width: 16 }}
                    checked={selectedModules.includes(module.key)}
                    onChange={() =>
                      setSelectedModules((current) =>
                        current.includes(module.key)
                          ? current.filter((key) => key !== module.key)
                          : [...current, module.key],
                      )
                    }
                  />
                  <span>
                    {module.name}{" "}
                    <span className="tiny faint">({module.question_count} questions)</span>
                  </span>
                </label>
              ))}
            </div>
          </Field>
        </Modal>
      ) : null}
    </>
  );
}
